import React, { useMemo, useState } from "react";
import {
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
	ComposedChart,
	Line,
	LabelList,
} from "recharts";
import DayWiseTable from "./DayWiseTable";
import OffersEngagementChart from "./OffersEngagementChart";
import { UsersEarningRangePanel } from "./UsersEarningDayPanel";

const safeNum = (v) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const safeRate = (v) => {
	const n = parseFloat(v);
	return Number.isFinite(n) ? n : 0;
};
const fmtMoneyK = (v) => `$${(safeNum(v) / 1000).toFixed(2)}`;

const badge = (letter, color) => (
	<span
		style={{
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			width: 22,
			height: 22,
			borderRadius: "50%",
			background: color,
			color: "#fff",
			fontSize: 11,
			fontWeight: 700,
			flexShrink: 0,
		}}
	>
		{letter}
	</span>
);

const getDayOffersDetail = (dayWiseData, day) => {
	const d = (dayWiseData ?? []).find((x) => x.day === day);
	return {
		categories: d?.offers?.categories ?? [],
		topOffers: d?.offers?.top_offers ?? [],
	};
};

const TABS = [
	{ key: "overview", label: "Overview" },
	{ key: "activation", label: "Revenue & Withdrawals" },
	{ key: "survey", label: "Survey Funnel" },
	{ key: "offers", label: "Offers & Categories" },
	{ key: "partners", label: "Partners" },
	{ key: "cohorts", label: "Cohorts" },
];

// ── shared cell styles ──────────────────────────────────────────
const thStyle = {
	padding: "12px 16px",
	textAlign: "left",
	background: "var(--bg-secondary, #F3F4F6)",
	fontWeight: 700,
	borderBottom: "2px solid var(--border-color, #E5E7EB)",
	whiteSpace: "nowrap",
};
const tdStyle = {
	padding: "11px 16px",
	borderBottom: "1px solid var(--border-color, #F3F4F6)",
};

const CAT_COLORS = [
	"#3B82F6", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899", "#06B6D4", "#EF4444", "#94A3B8",
];

// ── aggregation helpers across the whole range ──────────────────
// Day labels (D0..D15) derived from the most mature cohort in the range
const getDayOptions = (cohorts) => {
	let longest = [];
	(cohorts ?? []).forEach((c) => {
		const dw = c?.day_wise_data ?? [];
		if (dw.length > longest.length) longest = dw;
	});
	return longest.map((d) => String(d.day));
};

const aggregatePartnersForDay = (cohorts, day) => {
	const map = {};
	(cohorts ?? []).forEach((c) => {
		const entry = (c?.day_wise_data ?? []).find((d) => String(d.day) === String(day));
		(entry?.partner_earnings?.by_partner ?? []).forEach((p) => {
			if (!map[p.partner_id]) {
				map[p.partner_id] = { partner_id: p.partner_id, earners: 0, active_earners: 0, revenue: 0, payout: 0 };
			}
			map[p.partner_id].earners += safeNum(p.earners);
			map[p.partner_id].active_earners += safeNum(p.active_earners);
			map[p.partner_id].revenue += safeNum(p.revenue);
			map[p.partner_id].payout += safeNum(p.payout);
		});
	});
	return Object.values(map).sort((a, b) => b.revenue - a.revenue);
};

const daysOfCohort = (c, day) =>
	day != null
		? (c?.day_wise_data ?? []).filter((d) => String(d.day) === String(day))
		: c?.day_wise_data ?? [];

const aggregateCategories = (cohorts, day) => {
	const map = {};
	(cohorts ?? []).forEach((c) => {
		daysOfCohort(c, day).forEach((d) => {
			(d?.offers?.categories ?? []).forEach((cat) => {
				const key = cat.category ?? "Unknown";
				if (!map[key]) {
					map[key] = {
						category: key,
						total_clicks: 0,
						unique_clicks: 0,
						completed_clicks: 0,
						total_payout: 0,
					};
				}
				map[key].total_clicks += safeNum(cat.total_clicks);
				map[key].unique_clicks += safeNum(cat.unique_clicks);
				map[key].completed_clicks += safeNum(cat.completed_clicks);
				map[key].total_payout += safeNum(cat.total_payout);
			});
		});
	});
	return Object.values(map).sort((a, b) => b.total_payout - a.total_payout);
};

const aggregateTopOffers = (cohorts, day, limit = 20) => {
	const map = {};
	(cohorts ?? []).forEach((c) => {
		daysOfCohort(c, day).forEach((d) => {
			(d?.offers?.top_offers ?? []).forEach((o) => {
				const key = o._id ?? o.offerId ?? o.offer_name ?? "Unknown";
				if (!map[key]) {
					map[key] = {
						key,
						offer_name: o.offer_name ?? "—",
						total_users: 0,
						total_clicks: 0,
						completed_clicks: 0,
						total_payout: 0,
					};
				}
				map[key].total_users += safeNum(o.total_users);
				map[key].total_clicks += safeNum(o.total_clicks);
				map[key].completed_clicks += safeNum(o.completed_clicks);
				map[key].total_payout += safeNum(o.total_payout);
			});
		});
	});
	const sorted = Object.values(map).sort((a, b) => b.total_payout - a.total_payout);
	return limit == null ? sorted : sorted.slice(0, limit);
};

const MixShareTooltip = ({ active, payload, label }) => {
	if (!active || !payload?.length) return null;
	const counts = payload[0]?.payload?._counts ?? {};
	return (
		<div
			style={{
				background: "#fff",
				border: "1px solid #E5E7EB",
				borderRadius: 8,
				padding: "10px 14px",
				fontSize: 13,
				boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
			}}
		>
			<p style={{ fontWeight: 600, margin: "0 0 6px 0", color: "#111" }}>{label}</p>
			{payload.map((p, i) => {
				const count = counts[p.name] ?? 0;
				return (
					<p key={i} style={{ margin: "3px 0", color: p.color }}>
						{p.name}: <strong>{safeRate(p.value).toFixed(1)}% ({count.toLocaleString()})</strong>
					</p>
				);
			})}
		</div>
	);
};

const mixBarLabel = (dataKey) => (props) => {
	const { x, y, width, height, value, payload } = props;
	if (!value || value < 5 || height < 14) return null;
	const count = payload?._counts?.[dataKey] ?? 0;
	return (
		<text
			x={x + width / 2}
			y={y + height / 2}
			fill="#fff"
			textAnchor="middle"
			dominantBaseline="central"
			fontSize={9}
			fontWeight={700}
		>
			{`${Math.round(value)}% (${count.toLocaleString()})`}
		</text>
	);
};

const RangeTabs = ({ cohortData, partners, rangeStartDate, rangeEndDate }) => {
	const [activeTab, setActiveTab] = useState("overview");
	const [selectedIdx, setSelectedIdx] = useState(0);

	const agg = cohortData?.aggregate ?? {};
	const cohorts = cohortData?.cohorts ?? [];

	const totalClicks = cohorts.reduce(
		(sum, c) => sum + (c?.day_wise_data ?? []).reduce((s2, d) => s2 + safeNum(d?.offers?.total_clicks), 0),
		0
	);
	const totalCompleted = cohorts.reduce(
		(sum, c) => sum + (c?.day_wise_data ?? []).reduce((s2, d) => s2 + safeNum(d?.offers?.total_completed), 0),
		0
	);

	return (
		<div>
			{/* Range summary chip */}
			<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-lg)" }}>
				<div
					style={{
						display: "flex",
						gap: 12,
						flexWrap: "wrap",
						background: "#3B82F610",
						border: "1px solid #3B82F630",
						borderRadius: 8,
						padding: "10px 14px",
						alignItems: "center",
					}}
				>
					{badge("A", "#3B82F6")}
					<div style={{ fontSize: 13 }}>
						<span style={{ color: "#3B82F6", fontWeight: 700 }}>Range</span>
						<span style={{ color: "var(--text-muted)", marginLeft: 8 }}>
							{cohortData?.range?.start?.slice(0, 10) ?? rangeStartDate} →{" "}
							{cohortData?.range?.end?.slice(0, 10) ?? rangeEndDate}
						</span>
					</div>
					<div style={{ fontSize: 13, marginLeft: "auto" }}>
						<strong style={{ color: "#3B82F6" }}>{safeNum(agg.total_users).toLocaleString()}</strong>
						<span style={{ color: "var(--text-muted)" }}> users · </span>
						<strong style={{ color: "#3B82F6" }}>{safeNum(agg.total_cohorts).toLocaleString()}</strong>
						<span style={{ color: "var(--text-muted)" }}> cohorts</span>
					</div>
				</div>
			</div>

			{/* TAB BAR */}
			<div
				style={{
					display: "flex",
					gap: 6,
					flexWrap: "wrap",
					marginBottom: "var(--spacing-lg)",
				}}
			>
				{TABS.map((t) => {
					const active = activeTab === t.key;
					return (
						<button
							key={t.key}
							onClick={() => setActiveTab(t.key)}
							style={{
								padding: "10px 18px",
								border: active ? "1px solid #3B82F6" : "1px solid var(--border-color, #E5E7EB)",
								borderRadius: 10,
								fontSize: 14,
								fontWeight: 700,
								cursor: "pointer",
								transition: "all 0.15s",
								background: active ? "#3B82F6" : "var(--bg-card, #fff)",
								color: active ? "#fff" : "var(--text-secondary, #374151)",
								boxShadow: active ? "0 2px 6px rgba(59,130,246,0.3)" : "none",
							}}
						>
							{t.label}
						</button>
					);
				})}
			</div>

			{/* TAB CONTENT */}
			{activeTab === "overview" && (
				<OverviewTab agg={agg} cohorts={cohorts} totalClicks={totalClicks} totalCompleted={totalCompleted} />
			)}
			{activeTab === "activation" && <ActivationTab cohorts={cohorts} />}
			{activeTab === "survey" && <SurveyTab cohorts={cohorts} />}
			{activeTab === "offers" && (
				<OffersTab cohorts={cohorts} selectedIdx={selectedIdx} setSelectedIdx={setSelectedIdx} />
			)}
			{activeTab === "partners" && <PartnersTab cohorts={cohorts} partners={partners} />}
			{activeTab === "cohorts" && (
				<CohortsTab
					cohorts={cohorts}
					partners={partners}
					selectedIdx={selectedIdx}
					setSelectedIdx={setSelectedIdx}
				/>
			)}
		</div>
	);
};

// ────────────────────────────────────────────────────────────────
// OVERVIEW TAB
// ────────────────────────────────────────────────────────────────
const OverviewTab = ({ agg, cohorts, totalClicks, totalCompleted }) => {
	const kpis = [
		{ label: "Total Cohorts", value: safeNum(agg.total_cohorts).toLocaleString(), color: "#3B82F6" },
		{ label: "Total Users", value: safeNum(agg.total_users).toLocaleString(), color: "#10B981" },
		{ label: "Avg Users / Day", value: safeNum(agg.avg_users_per_day).toLocaleString(), color: "#8B5CF6" },
		{ label: "Total Offer Clicks", value: totalClicks.toLocaleString(), color: "#06B6D4" },
		{ label: "Total Completed", value: totalCompleted.toLocaleString(), color: "#EC4899" },
		{ label: "D0 Activation", value: `${safeRate(agg.activation?.d0_rate).toFixed(2)}%`, color: "#F59E0B" },
	];

	const dayOptions = useMemo(() => getDayOptions(cohorts), [cohorts]);
	const [selectedDays, setSelectedDays] = useState(() => {
		const def = ["D0", "D7", "D15"].filter((d) => dayOptions.includes(d));
		return def.length ? def : dayOptions.slice(0, 1);
	});
	const toggleDay = (d) =>
		setSelectedDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));

	const DAY_COLORS = [
		"#F59E0B", "#10B981", "#8B5CF6", "#EF4444", "#3B82F6", "#EC4899", "#06B6D4", "#84CC16",
		"#F97316", "#14B8A6", "#A855F7", "#0EA5E9", "#DC2626", "#65A30D", "#7C3AED", "#DB2777",
	];
	const colorForDay = (day) => DAY_COLORS[Math.max(dayOptions.indexOf(day), 0) % DAY_COLORS.length];

	const regData = cohorts.map((c) => {
		const row = { name: c?.cohort_date ?? "", registered: safeNum(c?.total_users) };
		dayOptions.forEach((day) => {
			const entry = (c?.day_wise_data ?? []).find((d) => String(d.day) === String(day));
			row[`act_${day}`] = safeRate(entry?.partner_earnings?.activation_rate);
		});
		return row;
	});
	const regValues = regData.map((r) => r.registered);
	const maxReg = regValues.length ? Math.max(...regValues) : 0;
	const minReg = regValues.length ? Math.min(...regValues) : 0;
	const avgD0 = regData.length ? regData.reduce((a, r) => a + (r["act_D0"] ?? 0), 0) / regData.length : 0;

	return (
		<>
			<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
				<h3 style={{ margin: "0 0 14px 0" }}>Range Summary</h3>
				<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "var(--spacing-lg)" }}>
					{kpis.map((c) => (
						<div
							key={c.label}
							style={{
								padding: 20,
								borderLeft: `4px solid ${c.color}`,
								backgroundColor: "white",
								borderRadius: 8,
								boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
							}}
						>
							<div style={{ marginBottom: 12 }}>
								<p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase" }}>
									{c.label}
								</p>
							</div>
							<p style={{ margin: 0, fontSize: 28, fontWeight: 700, color: c.color }}>{c.value}</p>
						</div>
					))}
				</div>
			</div>

			<div className="admin-form-container-v4">
				<h3 style={{ margin: "0 0 8px 0" }}>Registration Flow & D0 Activation</h3>
				<p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 12, marginTop: 0 }}>
					Daily registered users (bars) against activation rate (lines) for each selected day. Later days only show for cohorts old enough to be mature.
				</p>
				<div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
					<span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", marginRight: 4 }}>Activation days:</span>
					{dayOptions.map((d) => {
						const on = selectedDays.includes(d);
						const color = colorForDay(d);
						return (
							<button
								key={d}
								onClick={() => toggleDay(d)}
								style={{
									padding: "4px 10px",
									borderRadius: 999,
									fontSize: 12,
									fontWeight: 700,
									cursor: "pointer",
									border: `1px solid ${on ? color : "var(--border-color, #E5E7EB)"}`,
									background: on ? color : "var(--bg-card, #fff)",
									color: on ? "#fff" : "var(--text-muted)",
								}}
							>
								{d}
							</button>
						);
					})}
				</div>
				<ResponsiveContainer width="100%" height={320}>
					<ComposedChart data={regData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
						<defs>
							<linearGradient id="regBarFill" x1="0" y1="0" x2="0" y2="1">
								<stop offset="0%" stopColor="#3B82F6" stopOpacity={0.95} />
								<stop offset="100%" stopColor="#3B82F6" stopOpacity={0.55} />
							</linearGradient>
						</defs>
						<CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
						<XAxis dataKey="name" tick={{ fontSize: 12 }} />
						<YAxis
							yAxisId="left"
							tick={{ fontSize: 12 }}
							label={{ value: "Registered", angle: -90, position: "insideLeft", style: { fontSize: 12, fill: "#6B7280" } }}
						/>
						<YAxis
							yAxisId="right"
							orientation="right"
							domain={[0, 100]}
							unit="%"
							tick={{ fontSize: 12 }}
						/>
						<Tooltip
							formatter={(value, name) =>
								name === "Registered Users"
									? [safeNum(value).toLocaleString(), name]
									: [`${safeRate(value).toFixed(1)}%`, name]
							}
						/>
						<Legend wrapperStyle={{ fontSize: 12 }} />
						<Bar
							yAxisId="left"
							dataKey="registered"
							name="Registered Users"
							fill="url(#regBarFill)"
							radius={[4, 4, 0, 0]}
							maxBarSize={48}
						/>
						{selectedDays.map((day) => (
							<Line
								key={day}
								yAxisId="right"
								type="monotone"
								dataKey={`act_${day}`}
								name={`${day} Activation`}
								stroke={colorForDay(day)}
								strokeWidth={2.5}
								dot={{ r: 3, fill: colorForDay(day) }}
								activeDot={{ r: 6 }}
							/>
						))}
					</ComposedChart>
				</ResponsiveContainer>

				{regData.length > 0 && (
					<div
						style={{
							marginTop: 16,
							padding: "12px 16px",
							background: "var(--bg-secondary, #F9FAFB)",
							borderLeft: "4px solid #3B82F6",
							borderRadius: 8,
							fontSize: 13,
							color: "var(--text-secondary, #374151)",
						}}
					>
						Across <strong>{regData.length}</strong> day(s), registrations ranged from{" "}
						<strong>{minReg.toLocaleString()}</strong> to <strong>{maxReg.toLocaleString()}</strong> per day,
						with average D0 activation of <strong>{avgD0.toFixed(1)}%</strong>.
					</div>
				)}
			</div>

			<UsersEarningRangePanel cohorts={cohorts} />
		</>
	);
};

// ────────────────────────────────────────────────────────────────
// REVENUE & WITHDRAWALS TAB (per-day, across the range)
// ────────────────────────────────────────────────────────────────
const ActivationTab = ({ cohorts }) => {
	const dayOptions = useMemo(() => getDayOptions(cohorts), [cohorts]);

	const [dayFilter, setDayFilter] = useState(dayOptions[0] ?? "D0");
	const activeDay = dayOptions.includes(dayFilter) ? dayFilter : dayOptions[0] ?? "D0";

	const rows = (cohorts ?? []).map((c) => {
		const entry = (c?.day_wise_data ?? []).find((d) => String(d.day) === String(activeDay));
		return {
			name: c?.cohort_date ?? "",
			revenue: safeNum(entry?.partner_earnings?.total_revenue) / 1000,
			payout: safeNum(entry?.partner_earnings?.total_payout) / 1000,
			withdrawal: safeNum(entry?.withdrawals?.coins) / 1000,
			count: safeNum(entry?.withdrawals?.users),
		};
	});

	const n = rows.length || 1;
	const avg = {
		revenue: rows.reduce((a, r) => a + r.revenue, 0) / n,
		payout: rows.reduce((a, r) => a + r.payout, 0) / n,
		withdrawal: rows.reduce((a, r) => a + r.withdrawal, 0) / n,
		count: rows.reduce((a, r) => a + r.count, 0) / n,
	};

	const avgCards = [
		{ label: `Avg Revenue / cohort`, value: `$${avg.revenue.toFixed(2)}`, color: "#06B6D4" },
		{ label: `Avg Payout / cohort`, value: `$${avg.payout.toFixed(2)}`, color: "#EC4899" },
		{ label: `Avg Withdrawal / cohort`, value: `$${avg.withdrawal.toFixed(2)}`, color: "#F59E0B" },
		{ label: `Avg Withdrawal Count`, value: avg.count.toLocaleString(undefined, { maximumFractionDigits: 1 }), color: "#8B5CF6" },
	];

	return (
		<div className="admin-form-container-v4">
			<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
				<div>
					<h3 style={{ margin: 0 }}>Revenue & Withdrawals — {activeDay}</h3>
					<p style={{ fontSize: 13, color: "var(--text-muted)", margin: "4px 0 0 0" }}>
						Each cohort's revenue, payout and withdrawals on <strong>{activeDay}</strong>, across the selected range (money in $).
					</p>
				</div>
				<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
					<span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>Day:</span>
					<select
						value={activeDay}
						onChange={(e) => setDayFilter(e.target.value)}
						style={{
							padding: "8px 12px",
							border: "1px solid var(--border-color, #D1D5DB)",
							borderRadius: 8,
							fontSize: 13,
							fontWeight: 600,
							background: "var(--bg-card, #fff)",
							cursor: "pointer",
						}}
					>
						{dayOptions.map((d) => (
							<option key={d} value={d}>
								{d}
							</option>
						))}
					</select>
				</div>
			</div>

			<ResponsiveContainer width="100%" height={340}>
				<ComposedChart data={rows} margin={{ top: 12, right: 8, left: 0, bottom: 0 }}>
					<CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
					<XAxis dataKey="name" tick={{ fontSize: 12 }} />
					<YAxis
						yAxisId="left"
						tick={{ fontSize: 12 }}
						label={{ value: "$", angle: -90, position: "insideLeft", style: { fontSize: 12, fill: "#6B7280" } }}
					/>
					<YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} allowDecimals={false} />
					<Tooltip
						formatter={(value, name) =>
							name === "Withdrawal Count"
								? [safeNum(value).toLocaleString(), name]
								: [`$${safeNum(value).toFixed(2)}`, name]
						}
					/>
					<Legend wrapperStyle={{ fontSize: 12 }} />
					<Bar yAxisId="left" dataKey="revenue" name="Revenue" fill="#06B6D4" radius={[4, 4, 0, 0]} maxBarSize={26} />
					<Bar yAxisId="left" dataKey="payout" name="Payout" fill="#EC4899" radius={[4, 4, 0, 0]} maxBarSize={26} />
					<Bar yAxisId="left" dataKey="withdrawal" name="Withdrawal ($)" fill="#F59E0B" radius={[4, 4, 0, 0]} maxBarSize={26} />
					<Line
						yAxisId="right"
						type="monotone"
						dataKey="count"
						name="Withdrawal Count"
						stroke="#8B5CF6"
						strokeWidth={2.5}
						dot={{ r: 3, fill: "#8B5CF6" }}
						activeDot={{ r: 6 }}
					/>
				</ComposedChart>
			</ResponsiveContainer>

			{/* Range averages for the selected day */}
			<div style={{ marginTop: 20 }}>
				<p style={{ fontSize: 13, fontWeight: 700, color: "var(--text-secondary)", margin: "0 0 12px 0" }}>
					Range average on {activeDay} (per cohort)
				</p>
				<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "var(--spacing-lg)" }}>
					{avgCards.map((c) => (
						<div
							key={c.label}
							style={{
								padding: 18,
								borderLeft: `4px solid ${c.color}`,
								backgroundColor: "white",
								borderRadius: 8,
								boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
							}}
						>
							<p style={{ margin: "0 0 8px 0", fontSize: 12, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase" }}>
								{c.label}
							</p>
							<p style={{ margin: 0, fontSize: 26, fontWeight: 700, color: c.color }}>{c.value}</p>
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

// ────────────────────────────────────────────────────────────────
// SURVEY FUNNEL TAB — daily rates + funnel + drop-off
// ────────────────────────────────────────────────────────────────
// Survey happens on registration day, so we read each cohort's D0 survey block.
const surveyOfCohort = (c) => {
	const dw = c?.day_wise_data ?? [];
	const d0 = dw.find((d) => String(d.day) === "D0") ?? dw[0];
	return d0?.survey ?? null;
};

const SurveyTab = ({ cohorts }) => {
	// Distinct "other profiler" ids across the range → Other Profiler 1, 2, ...
	const otherIds = useMemo(() => {
		const ids = [];
		(cohorts ?? []).forEach((c) => {
			const s = surveyOfCohort(c);
			Object.keys(s?.other_profiler ?? {}).forEach((id) => {
				if (!ids.includes(id)) ids.push(id);
			});
		});
		return ids;
	}, [cohorts]);

	const rows = (cohorts ?? []).map((c) => {
		const s = surveyOfCohort(c);
		const reg = s?.registration_profiler;
		const others = s?.other_profiler ?? {};
		const partner = s?.partner_survey;

		const regRate = safeRate(reg?.completion_rate);
		const otherRates = otherIds.map((id) => safeRate(others[id]?.completion_rate));

		// Estimated "cleared all mandatory steps" = product of the step rates that were attempted
		const factors = [regRate, ...otherRates].filter((r) => r > 0).map((r) => r / 100);
		const combined = factors.length ? factors.reduce((a, b) => a * b, 1) * 100 : 0;

		const registered = safeNum(c?.total_users);
		const regCompleted = safeNum(reg?.completed);

		return {
			name: c?.cohort_date ?? "",
			registered,
			regRate,
			regCompleted,
			regNotCompleted: Math.max(registered - regCompleted, 0),
			otherRates,
			otherCompleted: otherIds.map((id) => safeNum(others[id]?.completed)),
			combined,
			combinedCount: Math.round((registered * combined) / 100),
			partnerRate: safeRate(partner?.completion_rate),
			partnerCompleted: safeNum(partner?.completed),
		};
	});

	// Range-level funnel: sum attempted/completed per step
	const stepTotals = useMemo(() => {
		const mk = (label) => ({ label, attempted: 0, completed: 0 });
		const reg = mk("Registration Profiler");
		const others = otherIds.map((id, i) => ({ ...mk(`Other Profiler ${i + 1}`), id }));
		const partner = mk("Partner Survey");
		(cohorts ?? []).forEach((c) => {
			const s = surveyOfCohort(c);
			reg.attempted += safeNum(s?.registration_profiler?.attempted);
			reg.completed += safeNum(s?.registration_profiler?.completed);
			others.forEach((o) => {
				o.attempted += safeNum(s?.other_profiler?.[o.id]?.attempted);
				o.completed += safeNum(s?.other_profiler?.[o.id]?.completed);
			});
			partner.attempted += safeNum(s?.partner_survey?.attempted);
			partner.completed += safeNum(s?.partner_survey?.completed);
		});
		return [reg, ...others, partner];
	}, [cohorts, otherIds]);

	const hasData = rows.some((r) => r.regRate > 0 || r.partnerRate > 0);
	if (!hasData) {
		return (
			<div className="admin-form-container-v4">
				<p style={{ textAlign: "center", color: "var(--text-muted)", padding: "40px 0" }}>
					No survey data for this range.
				</p>
			</div>
		);
	}

	const stepColors = ["#6366F1", "#8B5CF6", "#A855F7", "#0891B2"];

	return (
		<>
			{/* Daily trend */}
			<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
				<h3 style={{ margin: "0 0 4px 0" }}>Daily Survey Completion</h3>
				<p style={{ fontSize: 13, color: "var(--text-muted)", margin: "0 0 16px 0" }}>
					Per registration day — Registration Profiler, all-3 combined (est.) and Partner Survey completion rates, against registered users.
				</p>
				<ResponsiveContainer width="100%" height={330}>
					<ComposedChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
						<CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
						<XAxis dataKey="name" tick={{ fontSize: 12 }} />
						<YAxis yAxisId="left" tick={{ fontSize: 12 }} label={{ value: "Registered", angle: -90, position: "insideLeft", style: { fontSize: 12, fill: "#6B7280" } }} />
						<YAxis yAxisId="right" orientation="right" domain={[0, 100]} unit="%" tick={{ fontSize: 12 }} />
						<Tooltip
							formatter={(value, name, item) => {
								const p = item?.payload ?? {};
								if (name === "Registered") return [safeNum(value).toLocaleString(), name];
								if (name === "Registration Profiler")
									return [`${safeRate(value).toFixed(1)}% (${safeNum(p.regCompleted).toLocaleString()} of ${safeNum(p.registered).toLocaleString()})`, name];
								if (name === "All 3 Combined (est.)")
									return [`${safeRate(value).toFixed(1)}% (~${safeNum(p.combinedCount).toLocaleString()})`, name];
								if (name === "Partner Survey")
									return [`${safeRate(value).toFixed(1)}% (${safeNum(p.partnerCompleted).toLocaleString()})`, name];
								return [`${safeRate(value).toFixed(1)}%`, name];
							}}
						/>
						<Legend wrapperStyle={{ fontSize: 12 }} />
						<Bar yAxisId="left" dataKey="registered" name="Registered" fill="#94A3B8" radius={[4, 4, 0, 0]} maxBarSize={40} />
						<Line yAxisId="right" type="monotone" dataKey="regRate" name="Registration Profiler" stroke="#6366F1" strokeWidth={2.5} dot={{ r: 3, fill: "#6366F1" }} />
						<Line yAxisId="right" type="monotone" dataKey="combined" name="All 3 Combined (est.)" stroke="#16A34A" strokeWidth={2.5} dot={{ r: 3, fill: "#16A34A" }} />
						<Line yAxisId="right" type="monotone" dataKey="partnerRate" name="Partner Survey" stroke="#0891B2" strokeWidth={2.5} strokeDasharray="5 3" dot={{ r: 3, fill: "#0891B2" }} />
					</ComposedChart>
				</ResponsiveContainer>
			</div>

			{/* Range funnel + drop-off */}
			<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
				<h3 style={{ margin: "0 0 14px 0" }}>Funnel & Drop-off (whole range)</h3>
				<div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
					{stepTotals.map((s, i) => {
						const rate = s.attempted > 0 ? (s.completed / s.attempted) * 100 : 0;
						const notCompleted = Math.max(s.attempted - s.completed, 0);
						const color = stepColors[Math.min(i, stepColors.length - 1)];
						return (
							<div key={s.label}>
								<div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 13 }}>
									<span style={{ fontWeight: 700, color: "var(--text-primary)" }}>{s.label}</span>
									<span style={{ color: "var(--text-muted)" }}>
										<strong style={{ color }}>{s.completed.toLocaleString()}</strong> completed ·{" "}
										<strong style={{ color: "#EF4444" }}>{notCompleted.toLocaleString()}</strong> not completed ·{" "}
										{s.attempted.toLocaleString()} attempted
									</span>
								</div>
								<div style={{ display: "flex", height: 22, borderRadius: 6, overflow: "hidden", background: "#F3F4F6" }}>
									<div
										style={{
											width: `${Math.min(rate, 100)}%`,
											background: color,
											display: "flex",
											alignItems: "center",
											paddingLeft: 8,
											color: "#fff",
											fontSize: 11,
											fontWeight: 700,
										}}
									>
										{rate >= 14 ? `${rate.toFixed(1)}% · ${s.completed.toLocaleString()}` : ""}
									</div>
									{rate < 14 && (
										<span style={{ fontSize: 11, fontWeight: 700, color: color, marginLeft: 6, alignSelf: "center" }}>
											{rate.toFixed(1)}% · {s.completed.toLocaleString()}
										</span>
									)}
								</div>
							</div>
						);
					})}
				</div>
			</div>

			{/* Daily table */}
			<div className="admin-form-container-v4">
				<h3 style={{ margin: "0 0 14px 0" }}>Day-wise Rates</h3>
				<div style={{ overflowX: "auto" }}>
					<table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
						<thead>
							<tr>
								<th style={thStyle}>Date</th>
								<th style={{ ...thStyle, textAlign: "right" }}>Registered</th>
								<th style={{ ...thStyle, textAlign: "right" }}>Reg Profiler %</th>
								{otherIds.map((id, i) => (
									<th key={id} style={{ ...thStyle, textAlign: "right" }}>
										Other {i + 1} %
									</th>
								))}
								<th style={{ ...thStyle, textAlign: "right", color: "#16A34A" }}>Combined % (est.)</th>
								<th style={{ ...thStyle, textAlign: "right" }}>Partner %</th>
								<th style={{ ...thStyle, textAlign: "right", color: "#EF4444" }}>Reg Not Done</th>
							</tr>
						</thead>
						<tbody>
							{rows.map((r, i) => (
								<tr key={r.name || i} style={{ background: i % 2 === 0 ? "var(--bg-card, #fff)" : "var(--bg-secondary, #F9FAFB)" }}>
									<td style={{ ...tdStyle, fontWeight: 600, color: "var(--text-primary)" }}>{r.name}</td>
									<td style={{ ...tdStyle, textAlign: "right" }}>{r.registered.toLocaleString()}</td>
									<td style={{ ...tdStyle, textAlign: "right", fontWeight: 600, color: "#6366F1" }}>
										{r.regRate.toFixed(1)}%{" "}
										<span style={{ color: "var(--text-muted)", fontWeight: 400 }}>({r.regCompleted.toLocaleString()})</span>
									</td>
									{r.otherRates.map((rate, j) => (
										<td key={j} style={{ ...tdStyle, textAlign: "right" }}>
											{rate.toFixed(1)}%{" "}
											<span style={{ color: "var(--text-muted)", fontWeight: 400 }}>({(r.otherCompleted[j] ?? 0).toLocaleString()})</span>
										</td>
									))}
									<td style={{ ...tdStyle, textAlign: "right", fontWeight: 600, color: "#16A34A" }}>
										{r.combined.toFixed(1)}%{" "}
										<span style={{ color: "var(--text-muted)", fontWeight: 400 }}>(~{r.combinedCount.toLocaleString()})</span>
									</td>
									<td style={{ ...tdStyle, textAlign: "right" }}>
										{r.partnerRate.toFixed(1)}%{" "}
										<span style={{ color: "var(--text-muted)", fontWeight: 400 }}>({r.partnerCompleted.toLocaleString()})</span>
									</td>
									<td style={{ ...tdStyle, textAlign: "right", color: "#EF4444" }}>{r.regNotCompleted.toLocaleString()}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</>
	);
};

// ────────────────────────────────────────────────────────────────
// OFFERS & CATEGORIES TAB
// ────────────────────────────────────────────────────────────────
const OffersTab = ({ cohorts, selectedIdx, setSelectedIdx }) => {
	const selected = cohorts?.[selectedIdx];
	const compRate = (clicks, done) => (clicks > 0 ? ((done / clicks) * 100).toFixed(1) : "0.0");

	const dayOptions = useMemo(() => getDayOptions(cohorts), [cohorts]);
	const [dayFilter, setDayFilter] = useState(dayOptions[0] ?? "D0");
	const activeDay = dayOptions.includes(dayFilter) ? dayFilter : dayOptions[0] ?? "D0";

	const categoryRows = useMemo(() => aggregateCategories(cohorts, activeDay), [cohorts, activeDay]);
	const topOfferRows = useMemo(() => aggregateTopOffers(cohorts, activeDay, 20), [cohorts, activeDay]);
	const allOffersForMix = useMemo(
		() => [...aggregateTopOffers(cohorts, activeDay, null)].sort((a, b) => b.total_clicks - a.total_clicks),
		[cohorts, activeDay]
	);

	// Selected day's clicks/completion broken down per registration date
	const offersByCohort = useMemo(
		() =>
			(cohorts ?? []).map((c) => {
				const entry = (c?.day_wise_data ?? []).find((d) => String(d.day) === String(activeDay));
				const clicks = safeNum(entry?.offers?.total_clicks);
				const completed = safeNum(entry?.offers?.total_completed);
				return { name: c?.cohort_date ?? "", clicks, completed, rate: clicks > 0 ? (completed / clicks) * 100 : 0 };
			}),
		[cohorts, activeDay]
	);

	// Category mix (% share of clicks) per registration date — top categories + "Other"
	const catMix = useMemo(() => {
		const ranked = [...categoryRows].sort((a, b) => b.total_clicks - a.total_clicks).map((c) => c.category);
		const topNames = ranked.slice(0, 7);
		const catKeys = ranked.length > 7 ? [...topNames, "Other"] : topNames;
		const data = (cohorts ?? []).map((c) => {
			const entry = (c?.day_wise_data ?? []).find((d) => String(d.day) === String(activeDay));
			const cats = entry?.offers?.categories ?? [];
			const total = cats.reduce((s, x) => s + safeNum(x.total_clicks), 0);
			const row = { name: c?.cohort_date ?? "", _counts: {} };
			catKeys.forEach((k) => {
				row[k] = 0;
				row._counts[k] = 0;
			});
			cats.forEach((x) => {
				const clicks = safeNum(x.total_clicks);
				const key = topNames.includes(x.category) ? x.category : "Other";
				row[key] = (row[key] ?? 0) + (total > 0 ? (clicks / total) * 100 : 0);
				row._counts[key] = (row._counts[key] ?? 0) + clicks;
			});
			return row;
		});
		return { catKeys, data };
	}, [cohorts, activeDay, categoryRows]);

	// Offer mix (% share of clicks) per registration date — top 20 offers + "Other"
	const offerMix = useMemo(() => {
		const top20 = allOffersForMix.slice(0, 20);
		const topKeys = top20.map((o) => o.key);
		const keyName = {};
		allOffersForMix.forEach((o) => (keyName[o.key] = o.offer_name));
		const labels = top20.map((o) => o.offer_name);
		const seriesLabels = allOffersForMix.length > 20 ? [...labels, "Other"] : labels;
		const data = (cohorts ?? []).map((c) => {
			const entry = (c?.day_wise_data ?? []).find((d) => String(d.day) === String(activeDay));
			const offers = entry?.offers?.top_offers ?? [];
			const total = offers.reduce((s, x) => s + safeNum(x.total_clicks), 0);
			const row = { name: c?.cohort_date ?? "", _counts: {} };
			seriesLabels.forEach((l) => {
				row[l] = 0;
				row._counts[l] = 0;
			});
			offers.forEach((x) => {
				const clicks = safeNum(x.total_clicks);
				const k = x._id ?? x.offerId ?? x.offer_name;
				const label = topKeys.includes(k) ? keyName[k] ?? x.offer_name : "Other";
				row[label] = (row[label] ?? 0) + (total > 0 ? (clicks / total) * 100 : 0);
				row._counts[label] = (row._counts[label] ?? 0) + clicks;
			});
			return row;
		});
		return { seriesLabels, data };
	}, [cohorts, activeDay, allOffersForMix]);

	const categoryClickChart = useMemo(
		() =>
			categoryRows.map((c) => ({
				name: c.category,
				total_clicks: c.total_clicks,
				unique_clicks: c.unique_clicks,
			})),
		[categoryRows]
	);

	// Short analysis
	const totalClicks = categoryRows.reduce((s, c) => s + c.total_clicks, 0);
	const totalCompleted = categoryRows.reduce((s, c) => s + c.completed_clicks, 0);
	const totalPayout = categoryRows.reduce((s, c) => s + c.total_payout, 0);
	const overallComp = totalClicks > 0 ? ((totalCompleted / totalClicks) * 100).toFixed(1) : "0.0";
	const topCat = categoryRows[0];
	const topOffer = topOfferRows[0];
	const bestCompCat = [...categoryRows]
		.filter((c) => c.total_clicks > 0)
		.sort((a, b) => b.completed_clicks / b.total_clicks - a.completed_clicks / a.total_clicks)[0];

	const insights = [
		{ label: "Total Payout", value: fmtMoneyK(totalPayout), color: "#EC4899" },
		{ label: "Overall Completion", value: `${overallComp}%`, sub: `${totalCompleted.toLocaleString()} / ${totalClicks.toLocaleString()} clicks`, color: "#10B981" },
		{ label: "Top Category (payout)", value: topCat?.category ?? "—", sub: topCat ? fmtMoneyK(topCat.total_payout) : "", color: "#3B82F6" },
		{ label: "Best Completion Cat.", value: bestCompCat?.category ?? "—", sub: bestCompCat ? `${compRate(bestCompCat.total_clicks, bestCompCat.completed_clicks)}%` : "", color: "#F59E0B" },
		{ label: "Top Offer (payout)", value: topOffer?.offer_name ?? "—", sub: topOffer ? fmtMoneyK(topOffer.total_payout) : "", color: "#8B5CF6" },
	];

	const daySelector = (
		<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
			<span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>Day:</span>
			<select
				value={activeDay}
				onChange={(e) => setDayFilter(e.target.value)}
				style={{
					padding: "8px 12px",
					border: "1px solid var(--border-color, #D1D5DB)",
					borderRadius: 8,
					fontSize: 13,
					fontWeight: 600,
					background: "var(--bg-card, #fff)",
					cursor: "pointer",
				}}
			>
				{dayOptions.map((d) => (
					<option key={d} value={d}>
						{d}
					</option>
				))}
			</select>
		</div>
	);

	return (
		<>
			{/* Short analysis */}
			<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
					<h3 style={{ margin: 0 }}>Offers & Categories — {activeDay}</h3>
					{daySelector}
				</div>
				<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "var(--spacing-md)" }}>
					{insights.map((c) => (
						<div key={c.label} style={{ padding: 16, borderLeft: `4px solid ${c.color}`, backgroundColor: "white", borderRadius: 8, boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
							<p style={{ margin: "0 0 6px 0", fontSize: 12, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase" }}>{c.label}</p>
							<p style={{ margin: 0, fontSize: 18, fontWeight: 700, color: c.color, wordBreak: "break-word" }}>{c.value}</p>
							{c.sub && <p style={{ margin: "2px 0 0 0", fontSize: 12, color: "var(--text-muted)" }}>{c.sub}</p>}
						</div>
					))}
				</div>
			</div>

			{/* Clicks & completion by registration date */}
			<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
				<h3 style={{ margin: "0 0 4px 0" }}>{activeDay} Clicks &amp; Completion by Registration Date</h3>
				<p style={{ fontSize: 13, color: "var(--text-muted)", margin: "0 0 16px 0" }}>
					Each registration day's <strong>{activeDay}</strong> — bars = clicks vs completed, line = completion %. See how {activeDay} engagement differs day to day.
				</p>
				<ResponsiveContainer width="100%" height={340}>
					<ComposedChart data={offersByCohort} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
						<CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
						<XAxis dataKey="name" tick={{ fontSize: 12 }} />
						<YAxis yAxisId="left" tick={{ fontSize: 12 }} label={{ value: "Clicks", angle: -90, position: "insideLeft", style: { fontSize: 12, fill: "#6B7280" } }} />
						<YAxis yAxisId="right" orientation="right" domain={[0, 100]} unit="%" tick={{ fontSize: 12 }} />
						<Tooltip
							formatter={(value, name) =>
								name === "Completion %"
									? [`${safeRate(value).toFixed(1)}%`, name]
									: [safeNum(value).toLocaleString(), name]
							}
						/>
						<Legend wrapperStyle={{ fontSize: 12 }} />
						<Bar yAxisId="left" dataKey="clicks" name="Clicks" fill="#93C5FD" radius={[4, 4, 0, 0]} maxBarSize={30} />
						<Bar yAxisId="left" dataKey="completed" name="Completed" fill="#10B981" radius={[4, 4, 0, 0]} maxBarSize={30} />
						<Line yAxisId="right" type="monotone" dataKey="rate" name="Completion %" stroke="#F59E0B" strokeWidth={2.5} dot={{ r: 3, fill: "#F59E0B" }} activeDot={{ r: 6 }} />
					</ComposedChart>
				</ResponsiveContainer>
			</div>

			{/* Category mix per registration date */}
			{catMix.catKeys.length > 0 && (
				<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
					<h3 style={{ margin: "0 0 4px 0" }}>{activeDay} Category Mix by Registration Date</h3>
					<p style={{ fontSize: 13, color: "var(--text-muted)", margin: "0 0 16px 0" }}>
						Each registration day's <strong>{activeDay}</strong> clicks split by category (% share). Hover or read labels for click counts.
					</p>
					<ResponsiveContainer width="100%" height={360}>
						<ComposedChart data={catMix.data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
							<CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
							<XAxis dataKey="name" tick={{ fontSize: 12 }} />
							<YAxis domain={[0, 100]} unit="%" tick={{ fontSize: 12 }} />
							<Tooltip content={<MixShareTooltip />} />
							<Legend wrapperStyle={{ fontSize: 12 }} />
							{catMix.catKeys.map((k, i) => (
								<Bar key={k} dataKey={k} name={k} stackId="mix" fill={CAT_COLORS[i % CAT_COLORS.length]} maxBarSize={46}>
									<LabelList dataKey={k} content={mixBarLabel(k)} />
								</Bar>
							))}
						</ComposedChart>
					</ResponsiveContainer>
				</div>
			)}

			{/* Offer mix per registration date */}
			{offerMix.seriesLabels.length > 0 && (
				<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
					<h3 style={{ margin: "0 0 4px 0" }}>{activeDay} Offer Mix by Registration Date</h3>
					<p style={{ fontSize: 13, color: "var(--text-muted)", margin: "0 0 16px 0" }}>
						Each registration day's <strong>{activeDay}</strong> clicks split by top 20 offers (% share). Remaining offers are grouped as Other. Hover for click counts.
					</p>
					<ResponsiveContainer width="100%" height={360}>
						<ComposedChart data={offerMix.data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
							<CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
							<XAxis dataKey="name" tick={{ fontSize: 12 }} />
							<YAxis domain={[0, 100]} unit="%" tick={{ fontSize: 12 }} />
							<Tooltip content={<MixShareTooltip />} />
							<Legend wrapperStyle={{ fontSize: 11 }} />
							{offerMix.seriesLabels.map((k, i) => (
								<Bar key={k} dataKey={k} name={k} stackId="offmix" fill={CAT_COLORS[i % CAT_COLORS.length]} maxBarSize={46} />
							))}
						</ComposedChart>
					</ResponsiveContainer>
				</div>
			)}

			{/* Category performance */}
			<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
				<h3 style={{ margin: "0 0 14px 0" }}>Category Performance — {activeDay}</h3>
				{categoryRows.length ? (
					<>
						<p style={{ fontSize: 13, color: "var(--text-muted)", margin: "0 0 16px 0" }}>
							Total clicks vs unique clicks per category across the selected range.
						</p>
						<ResponsiveContainer width="100%" height={Math.max(280, categoryClickChart.length * 36)}>
							<ComposedChart
								data={categoryClickChart}
								layout="vertical"
								margin={{ top: 8, right: 48, left: 8, bottom: 0 }}
							>
								<CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} />
								<XAxis type="number" tick={{ fontSize: 12 }} />
								<YAxis type="category" dataKey="name" width={180} tick={{ fontSize: 11 }} />
								<Tooltip formatter={(value, name) => [safeNum(value).toLocaleString(), name]} />
								<Legend wrapperStyle={{ fontSize: 12 }} />
								<Bar dataKey="total_clicks" name="Total Clicks" fill="#93C5FD" radius={[0, 4, 4, 0]} maxBarSize={18}>
									<LabelList
										dataKey="total_clicks"
										position="right"
										formatter={(v) => safeNum(v).toLocaleString()}
										style={{ fontSize: 10, fontWeight: 600, fill: "#64748B" }}
									/>
								</Bar>
								<Bar dataKey="unique_clicks" name="Unique Clicks" fill="#3B82F6" radius={[0, 4, 4, 0]} maxBarSize={18}>
									<LabelList
										dataKey="unique_clicks"
										position="right"
										formatter={(v) => safeNum(v).toLocaleString()}
										style={{ fontSize: 10, fontWeight: 600, fill: "#1D4ED8" }}
									/>
								</Bar>
							</ComposedChart>
						</ResponsiveContainer>
						<div style={{ overflowX: "auto", marginTop: 20 }}>
							<table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
								<thead>
									<tr>
										<th style={thStyle}>Category</th>
										<th style={{ ...thStyle, textAlign: "right" }}>Clicks</th>
										<th style={{ ...thStyle, textAlign: "right" }}>Unique Clicks</th>
										<th style={{ ...thStyle, textAlign: "right" }}>Completed</th>
										<th style={{ ...thStyle, textAlign: "right" }}>Completion %</th>
										<th style={{ ...thStyle, textAlign: "right" }}>Payout</th>
									</tr>
								</thead>
								<tbody>
									{categoryRows.map((c, i) => (
										<tr key={c.category} style={{ background: i % 2 === 0 ? "var(--bg-card, #fff)" : "var(--bg-secondary, #F9FAFB)" }}>
											<td style={{ ...tdStyle, fontWeight: 600, color: "var(--text-primary)" }}>{c.category}</td>
											<td style={{ ...tdStyle, textAlign: "right" }}>{c.total_clicks.toLocaleString()}</td>
											<td style={{ ...tdStyle, textAlign: "right", fontWeight: 600, color: "#3B82F6" }}>{c.unique_clicks.toLocaleString()}</td>
											<td style={{ ...tdStyle, textAlign: "right", color: "#10B981", fontWeight: 600 }}>{c.completed_clicks.toLocaleString()}</td>
											<td style={{ ...tdStyle, textAlign: "right" }}>{compRate(c.total_clicks, c.completed_clicks)}%</td>
											<td style={{ ...tdStyle, textAlign: "right", fontWeight: 600 }}>{fmtMoneyK(c.total_payout)}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</>
				) : (
					<p style={{ textAlign: "center", color: "var(--text-muted)", padding: "30px 0" }}>No category data.</p>
				)}
			</div>

			{/* Top offers */}
			<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
				<h3 style={{ margin: "0 0 14px 0" }}>Top Offers (by payout) — {activeDay}</h3>
				{topOfferRows.length ? (
					<div style={{ overflowX: "auto" }}>
						<table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
							<thead>
								<tr>
									<th style={thStyle}>#</th>
									<th style={thStyle}>Offer</th>
									<th style={{ ...thStyle, textAlign: "right" }}>Users</th>
									<th style={{ ...thStyle, textAlign: "right" }}>Clicks</th>
									<th style={{ ...thStyle, textAlign: "right" }}>Completed</th>
									<th style={{ ...thStyle, textAlign: "right" }}>Completion %</th>
									<th style={{ ...thStyle, textAlign: "right" }}>Payout</th>
								</tr>
							</thead>
							<tbody>
								{topOfferRows.map((o, i) => (
									<tr key={o.key} style={{ background: i % 2 === 0 ? "var(--bg-card, #fff)" : "var(--bg-secondary, #F9FAFB)" }}>
										<td style={tdStyle}>{i + 1}</td>
										<td style={{ ...tdStyle, fontWeight: 600, color: "var(--text-primary)" }}>{o.offer_name}</td>
										<td style={{ ...tdStyle, textAlign: "right" }}>{o.total_users.toLocaleString()}</td>
										<td style={{ ...tdStyle, textAlign: "right" }}>{o.total_clicks.toLocaleString()}</td>
										<td style={{ ...tdStyle, textAlign: "right", color: "#10B981", fontWeight: 600 }}>{o.completed_clicks.toLocaleString()}</td>
										<td style={{ ...tdStyle, textAlign: "right" }}>{compRate(o.total_clicks, o.completed_clicks)}%</td>
										<td style={{ ...tdStyle, textAlign: "right", fontWeight: 600 }}>{fmtMoneyK(o.total_payout)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				) : (
					<p style={{ textAlign: "center", color: "var(--text-muted)", padding: "30px 0" }}>No offer data.</p>
				)}
			</div>

			{/* Engagement trend for a selected cohort */}
			{selected?.summary?.offers_engagement_by_day?.length > 0 && (
				<div className="admin-form-container-v4">
					<h3 style={{ margin: "0 0 10px 0" }}>Offers Engagement Trend (D0-D15)</h3>
					<div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
						<span style={{ fontSize: 13, color: "var(--text-muted)" }}>Cohort:</span>
						<select
							value={selectedIdx}
							onChange={(e) => setSelectedIdx(Number(e.target.value))}
							style={{
								padding: "6px 10px",
								border: "1px solid var(--border-color, #D1D5DB)",
								borderRadius: 6,
								fontSize: 13,
								background: "var(--bg-card, #fff)",
								cursor: "pointer",
							}}
						>
							{cohorts.map((c, i) => (
								<option key={c?.cohort_date ?? i} value={i}>
									{c?.cohort_date ?? "—"} ({safeNum(c?.total_users).toLocaleString()} users)
								</option>
							))}
						</select>
					</div>
					<OffersEngagementChart
						seriesA={{ label: "Cohort", color: "#3B82F6", data: selected.summary.offers_engagement_by_day }}
						getDetailA={(day) => getDayOffersDetail(selected.day_wise_data, day)}
					/>
				</div>
			)}
		</>
	);
};

// ────────────────────────────────────────────────────────────────
// PARTNERS TAB
// ────────────────────────────────────────────────────────────────
const PartnersTab = ({ cohorts, partners }) => {
	const dayOptions = useMemo(() => getDayOptions(cohorts), [cohorts]);
	const [dayFilter, setDayFilter] = useState(dayOptions[0] ?? "D0");
	const activeDay = dayOptions.includes(dayFilter) ? dayFilter : dayOptions[0] ?? "D0";

	const partnerRows = useMemo(() => aggregatePartnersForDay(cohorts, activeDay), [cohorts, activeDay]);

	// Partner filter for the "by registration date" breakdown
	const [selectedPartner, setSelectedPartner] = useState("all");
	const partnerOptions = partnerRows.map((p) => ({ id: p.partner_id, name: partners[p.partner_id]?.name || "Unknown Partner" }));
	const activePartner = selectedPartner === "all" || partnerOptions.some((o) => o.id === selectedPartner) ? selectedPartner : "all";

	// Selected day (e.g. D0) broken down per registration date, optionally for one partner
	const totalsByCohort = useMemo(
		() =>
			(cohorts ?? []).map((c) => {
				let earners = 0,
					active = 0,
					revenue = 0,
					payout = 0;
				const entry = (c?.day_wise_data ?? []).find((d) => String(d.day) === String(activeDay));
				(entry?.partner_earnings?.by_partner ?? []).forEach((p) => {
					if (activePartner !== "all" && p.partner_id !== activePartner) return;
					earners += safeNum(p.earners);
					active += safeNum(p.active_earners);
					revenue += safeNum(p.revenue);
					payout += safeNum(p.payout);
				});
				return { name: c?.cohort_date ?? "", earners, active_earners: active, revenue, payout };
			}),
		[cohorts, activeDay, activePartner]
	);

	const daySelector = (
		<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
			<span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>Day:</span>
			<select
				value={activeDay}
				onChange={(e) => setDayFilter(e.target.value)}
				style={{
					padding: "8px 12px",
					border: "1px solid var(--border-color, #D1D5DB)",
					borderRadius: 8,
					fontSize: 13,
					fontWeight: 600,
					background: "var(--bg-card, #fff)",
					cursor: "pointer",
				}}
			>
				{dayOptions.map((d) => (
					<option key={d} value={d}>
						{d}
					</option>
				))}
			</select>
		</div>
	);

	if (!partnerRows.length) {
		return (
			<div className="admin-form-container-v4">
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
					<h3 style={{ margin: 0 }}>Partner Performance — {activeDay}</h3>
					{daySelector}
				</div>
				<p style={{ textAlign: "center", color: "var(--text-muted)", padding: "40px 0" }}>
					No partner data on {activeDay} for this range.
				</p>
			</div>
		);
	}
	const top = partnerRows.slice(0, 10);
	const chartData = top.map((p) => ({
		name: partners[p.partner_id]?.name || "Unknown",
		earners: p.earners,
		active_earners: p.active_earners,
		revenue: p.revenue,
		payout: p.payout,
	}));

	return (
		<>
			<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
					<div>
						<h3 style={{ margin: 0 }}>Partner Performance — {activeDay}, Top 10</h3>
						<p style={{ fontSize: 13, color: "var(--text-muted)", margin: "4px 0 0 0" }}>
							Bars = users (clicked vs earned), lines = money (revenue vs payout) on <strong>{activeDay}</strong>, summed across every cohort.
						</p>
					</div>
					{daySelector}
				</div>
				<ResponsiveContainer width="100%" height={400}>
					<ComposedChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 70 }}>
						<CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
						<XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-25} textAnchor="end" interval={0} height={80} />
						<YAxis
							yAxisId="left"
							tick={{ fontSize: 12 }}
							label={{ value: "Users", angle: -90, position: "insideLeft", style: { fontSize: 12, fill: "#6B7280" } }}
						/>
						<YAxis
							yAxisId="right"
							orientation="right"
							tick={{ fontSize: 12 }}
							label={{ value: "Coins", angle: 90, position: "insideRight", style: { fontSize: 12, fill: "#6B7280" } }}
						/>
						<Tooltip
							formatter={(value, name) =>
								name === "Revenue" || name === "Payout"
									? [`${safeNum(value).toLocaleString()} coins`, name]
									: [safeNum(value).toLocaleString(), name]
							}
						/>
						<Legend wrapperStyle={{ fontSize: 12 }} />
						<Bar yAxisId="left" dataKey="earners" name="Earners (clicked)" fill="#C4B5FD" radius={[4, 4, 0, 0]} maxBarSize={26} />
						<Bar yAxisId="left" dataKey="active_earners" name="Active Earners (earned)" fill="#8B5CF6" radius={[4, 4, 0, 0]} maxBarSize={26} />
						<Line yAxisId="right" type="monotone" dataKey="revenue" name="Revenue" stroke="#F59E0B" strokeWidth={2.5} dot={{ r: 3, fill: "#F59E0B" }} />
						<Line yAxisId="right" type="monotone" dataKey="payout" name="Payout" stroke="#EF4444" strokeWidth={2} strokeDasharray="5 3" dot={{ r: 3, fill: "#EF4444" }} />
					</ComposedChart>
				</ResponsiveContainer>
			</div>

			{/* {activeDay} broken down per registration date */}
			<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 4 }}>
					<h3 style={{ margin: 0 }}>{activeDay} by Registration Date</h3>
					<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
						<span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>Partner:</span>
						<select
							value={activePartner}
							onChange={(e) => setSelectedPartner(e.target.value)}
							style={{
								padding: "8px 12px",
								border: "1px solid var(--border-color, #D1D5DB)",
								borderRadius: 8,
								fontSize: 13,
								fontWeight: 600,
								background: "var(--bg-card, #fff)",
								cursor: "pointer",
								maxWidth: 220,
							}}
						>
							<option value="all">All Partners</option>
							{partnerOptions.map((o) => (
								<option key={o.id} value={o.id}>
									{o.name}
								</option>
							))}
						</select>
					</div>
				</div>
				<p style={{ fontSize: 13, color: "var(--text-muted)", margin: "0 0 16px 0" }}>
					{activePartner === "all" ? "All partners combined" : partnerOptions.find((o) => o.id === activePartner)?.name}'s{" "}
					<strong>{activeDay}</strong> per registration date — bars = users, lines = money. Compare how {activeDay} differs day to day.
				</p>
				<ResponsiveContainer width="100%" height={340}>
					<ComposedChart data={totalsByCohort} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
						<CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
						<XAxis dataKey="name" tick={{ fontSize: 12 }} />
						<YAxis yAxisId="left" tick={{ fontSize: 12 }} label={{ value: "Users", angle: -90, position: "insideLeft", style: { fontSize: 12, fill: "#6B7280" } }} />
						<YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} label={{ value: "Coins", angle: 90, position: "insideRight", style: { fontSize: 12, fill: "#6B7280" } }} />
						<Tooltip
							formatter={(value, name) =>
								name === "Revenue" || name === "Payout"
									? [`${safeNum(value).toLocaleString()} coins`, name]
									: [safeNum(value).toLocaleString(), name]
							}
						/>
						<Legend wrapperStyle={{ fontSize: 12 }} />
						<Bar yAxisId="left" dataKey="earners" name="Earners (clicked)" fill="#C4B5FD" radius={[4, 4, 0, 0]} maxBarSize={30} />
						<Bar yAxisId="left" dataKey="active_earners" name="Active Earners (earned)" fill="#8B5CF6" radius={[4, 4, 0, 0]} maxBarSize={30} />
						<Line yAxisId="right" type="monotone" dataKey="revenue" name="Revenue" stroke="#F59E0B" strokeWidth={2.5} dot={{ r: 3, fill: "#F59E0B" }} />
						<Line yAxisId="right" type="monotone" dataKey="payout" name="Payout" stroke="#EF4444" strokeWidth={2} strokeDasharray="5 3" dot={{ r: 3, fill: "#EF4444" }} />
					</ComposedChart>
				</ResponsiveContainer>

				<div style={{ overflowX: "auto", marginTop: 20 }}>
					<table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
						<thead>
							<tr>
								<th style={thStyle}>Registration Date</th>
								<th style={{ ...thStyle, textAlign: "right" }}>Earners</th>
								<th style={{ ...thStyle, textAlign: "right", color: "#16A34A" }}>Active Earners</th>
								<th style={{ ...thStyle, textAlign: "right" }}>Revenue</th>
								<th style={{ ...thStyle, textAlign: "right" }}>Payout</th>
							</tr>
						</thead>
						<tbody>
							{totalsByCohort.map((d, i) => (
								<tr key={d.name || i} style={{ background: i % 2 === 0 ? "var(--bg-card, #fff)" : "var(--bg-secondary, #F9FAFB)" }}>
									<td style={{ ...tdStyle, fontWeight: 600, color: "var(--text-primary)" }}>{d.name}</td>
									<td style={{ ...tdStyle, textAlign: "right" }}>{d.earners.toLocaleString()}</td>
									<td style={{ ...tdStyle, textAlign: "right", fontWeight: 600, color: "#16A34A" }}>{d.active_earners.toLocaleString()}</td>
									<td style={{ ...tdStyle, textAlign: "right" }}>{d.revenue.toLocaleString(undefined, { maximumFractionDigits: 1 })} coins</td>
									<td style={{ ...tdStyle, textAlign: "right" }}>{d.payout.toLocaleString(undefined, { maximumFractionDigits: 1 })} coins</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>

			<div className="admin-form-container-v4">
				<h3 style={{ margin: "0 0 14px 0" }}>All Partners — {activeDay}</h3>
				<div style={{ overflowX: "auto" }}>
					<table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
						<thead>
							<tr>
								<th style={thStyle}>#</th>
								<th style={thStyle}>Partner</th>
								<th style={{ ...thStyle, textAlign: "right" }}>Earners (clicked)</th>
								<th style={{ ...thStyle, textAlign: "right", color: "#16A34A" }}>Active Earners (earned)</th>
								<th style={{ ...thStyle, textAlign: "right" }}>Revenue</th>
								<th style={{ ...thStyle, textAlign: "right" }}>Payout</th>
							</tr>
						</thead>
						<tbody>
							{partnerRows.map((p, i) => (
								<tr key={p.partner_id ?? i} style={{ background: i % 2 === 0 ? "var(--bg-card, #fff)" : "var(--bg-secondary, #F9FAFB)" }}>
									<td style={tdStyle}>{i + 1}</td>
									<td style={{ ...tdStyle, fontWeight: 600, color: "var(--text-primary)" }}>
										<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
											{partners[p.partner_id]?.image && (
												<img
													src={partners[p.partner_id].image}
													alt=""
													style={{ width: 24, height: 24, borderRadius: 6, objectFit: "cover" }}
												/>
											)}
											{partners[p.partner_id]?.name || "Unknown Partner"}
										</div>
									</td>
									<td style={{ ...tdStyle, textAlign: "right" }}>{p.earners.toLocaleString()}</td>
									<td style={{ ...tdStyle, textAlign: "right", fontWeight: 600, color: "#16A34A" }}>{p.active_earners.toLocaleString()}</td>
									<td style={{ ...tdStyle, textAlign: "right", fontWeight: 600, color: "#8B5CF6" }}>
										{p.revenue.toLocaleString(undefined, { maximumFractionDigits: 1 })} coins
									</td>
									<td style={{ ...tdStyle, textAlign: "right" }}>
										{p.payout.toLocaleString(undefined, { maximumFractionDigits: 1 })} coins
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</>
	);
};

// ────────────────────────────────────────────────────────────────
// COHORTS TAB
// ────────────────────────────────────────────────────────────────
const CohortsTab = ({ cohorts, partners, selectedIdx, setSelectedIdx }) => {
	const selected = cohorts?.[selectedIdx];
	const dayWise = selected?.day_wise_data ?? [];

	return (
		<>
			<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
				<h3 style={{ margin: "0 0 12px 0" }}>Cohorts in Range</h3>
				<div style={{ overflowX: "auto" }}>
					<table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
						<thead>
							<tr>
								<th style={{ ...thStyle, padding: "12px 14px" }}>Cohort Date</th>
								<th style={{ ...thStyle, padding: "12px 14px", textAlign: "right" }}>Users</th>
								<th style={{ ...thStyle, padding: "12px 14px", textAlign: "right" }}>Total Clicks</th>
								<th style={{ ...thStyle, padding: "12px 14px", textAlign: "right" }}>Completed</th>
								<th style={{ ...thStyle, padding: "12px 14px", textAlign: "right" }}>D0%</th>
								<th style={{ ...thStyle, padding: "12px 14px", textAlign: "right" }}>D7%</th>
								<th style={{ ...thStyle, padding: "12px 14px", textAlign: "right" }}>D15%</th>
								<th style={{ ...thStyle, padding: "12px 14px", textAlign: "right" }}>D15 Revenue</th>
								<th style={{ ...thStyle, padding: "12px 14px", textAlign: "right" }}>D15 Payout</th>
								<th style={{ ...thStyle, padding: "12px 14px", textAlign: "right" }}>D15 Withdrawals</th>
							</tr>
						</thead>
						<tbody>
							{cohorts.map((c, idx) => {
								const s = c?.summary;
								const tClicks = (c?.day_wise_data ?? []).reduce((sum, d) => sum + safeNum(d?.offers?.total_clicks), 0);
								const tCompleted = (c?.day_wise_data ?? []).reduce((sum, d) => sum + safeNum(d?.offers?.total_completed), 0);
								return (
									<tr
										key={c?.cohort_date ?? idx}
										onClick={() => setSelectedIdx(idx)}
										style={{
											background: idx === selectedIdx ? "#3B82F615" : idx % 2 === 0 ? "var(--bg-card, #fff)" : "var(--bg-secondary, #F9FAFB)",
											cursor: "pointer",
										}}
									>
										<td style={{ ...tdStyle, padding: "11px 14px", fontWeight: 600, color: "var(--text-primary)" }}>{c?.cohort_date ?? "—"}</td>
										<td style={{ ...tdStyle, padding: "11px 14px", textAlign: "right" }}>{safeNum(c?.total_users).toLocaleString()}</td>
										<td style={{ ...tdStyle, padding: "11px 14px", textAlign: "right", fontWeight: 600, color: "#3B82F6" }}>{tClicks.toLocaleString()}</td>
										<td style={{ ...tdStyle, padding: "11px 14px", textAlign: "right", fontWeight: 600, color: "#10B981" }}>{tCompleted.toLocaleString()}</td>
										<td style={{ ...tdStyle, padding: "11px 14px", textAlign: "right" }}>{s?.activation ? `${safeRate(s.activation?.d0_rate).toFixed(2)}%` : "—"}</td>
										<td style={{ ...tdStyle, padding: "11px 14px", textAlign: "right" }}>{s?.activation ? `${safeRate(s.activation?.d7_rate).toFixed(2)}%` : "—"}</td>
										<td style={{ ...tdStyle, padding: "11px 14px", textAlign: "right" }}>{s?.activation ? `${safeRate(s.activation?.d15_rate).toFixed(2)}%` : "—"}</td>
										<td style={{ ...tdStyle, padding: "11px 14px", textAlign: "right" }}>{s?.revenue ? fmtMoneyK(s.revenue?.d15_cumulative) : "—"}</td>
										<td style={{ ...tdStyle, padding: "11px 14px", textAlign: "right" }}>{s?.payout ? fmtMoneyK(s.payout?.d15_cumulative) : "—"}</td>
										<td style={{ ...tdStyle, padding: "11px 14px", textAlign: "right" }}>
											{s?.withdrawals
												? `${safeNum(s.withdrawals?.d15_users).toLocaleString()} users · ${safeNum(s.withdrawals?.d15_coins).toLocaleString()} coins`
												: "—"}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</div>

			<div className="admin-form-container-v4">
				<h3 style={{ margin: "0 0 10px 0" }}>Day-wise Breakdown</h3>
				<p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16, marginTop: 0 }}>
					Select a cohort date (or click a row above) to view its D0–D15 performance.
				</p>
				{cohorts.length ? (
					<>
						<div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
							{badge("A", "#3B82F6")}
							<select
								value={selectedIdx}
								onChange={(e) => setSelectedIdx(Number(e.target.value))}
								style={{
									padding: "6px 10px",
									border: "1px solid var(--border-color, #D1D5DB)",
									borderRadius: 6,
									fontSize: 13,
									background: "var(--bg-card, #fff)",
									cursor: "pointer",
								}}
							>
								{cohorts.map((c, i) => (
									<option key={c?.cohort_date ?? i} value={i}>
										{c?.cohort_date ?? "—"} ({safeNum(c?.total_users).toLocaleString()} users)
									</option>
								))}
							</select>
						</div>
						{dayWise.length ? (
							<DayWiseTable dayWiseData={dayWise} partners={partners} totalUsers={safeNum(selected?.total_users)} />
						) : (
							<p style={{ textAlign: "center", color: "var(--text-muted)", padding: "40px 0" }}>No day-wise data for this cohort.</p>
						)}
					</>
				) : (
					<p style={{ textAlign: "center", color: "var(--text-muted)", padding: "40px 0" }}>No cohorts available.</p>
				)}
			</div>
		</>
	);
};

export default RangeTabs;
